import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", id: "nav-home-link" },
  { to: "/services", label: "Services", id: "nav-services-link" },
  { to: "/rolesense", label: "RoleSense™", id: "nav-rolesense-link" },
  { to: "/success-stories", label: "Success Stories", id: "nav-success-stories-link" },
  { to: "/about-contact", label: "About & Contact", id: "nav-about-contact-link" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  return (
    <header className="fixed inset-x-0 top-0 z-50" data-testid="site-header">
      <div className="border-b border-line bg-paper/85 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="group flex flex-col leading-none" data-testid="brand-logo-link" onClick={() => setOpen(false)}>
            <span className="font-serif text-xl tracking-tight text-ink">
              ALLY <span className="italic text-gold-dark">eXecutive</span> HR
            </span>
            <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.28em] text-ink-faded">
              Search · Consulting · Advisory
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.id}
                className={({ isActive }) =>
                  `link-sweep text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold-dark" : "text-ink-muted hover:text-ink"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/about-contact#contact"
              data-testid="hire-talent-cta-button"
              className="group inline-flex items-center gap-2 border border-ink bg-ink px-5 py-2.5 text-[13px] font-semibold tracking-wide text-paper transition-colors duration-300 hover:bg-gold hover:border-gold hover:text-ink"
            >
              Hire Talent
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            className="lg:hidden text-ink"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        <motion.div
          className="h-[2px] origin-left bg-gold"
          style={{ scaleX: progress }}
          data-testid="scroll-progress-bar"
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={pathname + "-mobile"}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-b border-line bg-paper px-6 py-6 lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-5">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-${item.id}`}
                  className={({ isActive }) =>
                    `font-serif text-2xl ${isActive ? "text-gold-dark" : "text-ink"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/about-contact#contact"
                onClick={() => setOpen(false)}
                data-testid="mobile-hire-talent-cta-button"
                className="mt-2 inline-flex w-fit items-center gap-2 border border-ink bg-ink px-6 py-3 text-sm font-semibold text-paper"
              >
                Hire Talent <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
